import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Flag,
  Mail,
  FileText,
  Edit2,
  Phone as PhoneIcon,
  CheckCircle,
  X,
  Plus,
  ArrowLeft,
  Tag,
  Search,
  User,
  Building2,
} from 'lucide-react';
import { api, apiClient } from '../services/api';
import { exportAndDownload } from '../utils/downloadPdf';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import EditContactModal from '../components/EditContactModal';
import Avatar from '../components/Avatar';
import AgencyLogo from '../components/AgencyLogo';
import type { Contact, Note, AuditEntry, Flag as FlagType } from '../types';

type Tab = 'overview' | 'phones' | 'roles' | 'notes' | 'flags' | 'audit';

const PHONE_TYPES: Record<number, string> = {
  1: 'Office',
  2: 'Mobile',
  3: 'Fax',
  4: 'Home',
  5: 'Direct',
  6: 'Other',
};

interface EditRolesModalProps {
  contactId: string;
  allRoles: any[];
  currentRoleIds: string[];
  onClose: () => void;
  onSaved: () => void;
}

const EditRolesModal: React.FC<EditRolesModalProps> = ({ contactId, allRoles, currentRoleIds, onClose, onSaved }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set(currentRoleIds));
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);

  const filtered = useMemo(() =>
    allRoles.filter((r) => r.name.toLowerCase().includes(search.toLowerCase())),
    [allRoles, search]
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setWarnings([]);
    try {
      const result = await api.assignRoles(contactId, Array.from(selected));
      if (result?.warnings?.length) {
        setWarnings(result.warnings);
        setTimeout(onSaved, 2000);
      } else {
        onSaved();
      }
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Failed to save roles.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Edit Roles</h3>
          <button className="btn btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
          <input
            type="text"
            placeholder="Filter roles…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '7px 10px 7px 30px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
          />
        </div>
        {warnings.length > 0 && (
          <div style={{ background: 'var(--amber-50)', border: '1px solid var(--amber)', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
            {warnings.map((w, i) => <p key={i} style={{ margin: 0, fontSize: 12, color: 'var(--amber-text)' }}>{w}</p>)}
          </div>
        )}
        <div style={{ overflowY: 'auto', flex: 1, border: '1px solid var(--gray-200)', borderRadius: 6 }}>
          {filtered.map((r) => (
            <label
              key={r.id}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)', fontSize: 14 }}
            >
              <input
                type="checkbox"
                checked={selected.has(r.id)}
                onChange={() => toggle(r.id)}
                style={{ width: 15, height: 15 }}
              />
              {r.name}
            </label>
          ))}
          {filtered.length === 0 && (
            <p style={{ color: 'var(--gray-500)', padding: 12, margin: 0, fontSize: 14 }}>No roles match.</p>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{selected.size} selected</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={saving} onClick={save}>
              {saving ? 'Saving…' : 'Save Roles'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { agingDays, overdueDays } = useSettings();
  const qc = useQueryClient();

  const [tab, setTab] = useState<Tab>('overview');
  const [showRequestPopup, setShowRequestPopup] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditRolesModal, setShowEditRolesModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveFlagId, setResolveFlagId] = useState<string | null>(null);
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [sendEmail, setSendEmail] = useState('');
  const [sendMsg, setSendMsg] = useState('');
  const [newNote, setNewNote] = useState('');
  const [noteError, setNoteError] = useState('');

  const { data: contact, isLoading, error } = useQuery<Contact>({
    queryKey: ['contact', id],
    queryFn: () => api.getContact(id!),
    enabled: !!id,
  });

  const { data: contactRoles = [], refetch: refetchContactRoles } = useQuery<any[]>({
    queryKey: ['contact-roles', id],
    queryFn: () => api.getContactRoles(id!),
    enabled: !!id,
  });

  const { data: allRoles = [] } = useQuery<any[]>({
    queryKey: ['roles-all'],
    queryFn: () => api.getRoles(),
  });

  const flagMutation = useMutation({
    mutationFn: () => api.flagContact(id!, { flag_reason: flagReason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact', id] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['flags'] });
      qc.invalidateQueries({ queryKey: ['kpi'] });
      window.dispatchEvent(new Event('iad2:counts-stale'));
      setShowFlagModal(false);
      setFlagReason('');
    },
  });

  const sendMutation = useMutation({
    mutationFn: () => api.sendContact(id!, { recipient_email: sendEmail, message: sendMsg || null }),
    onSuccess: () => {
      setShowSendModal(false);
      setSendEmail('');
      setSendMsg('');
      alert('Contact info sent successfully.');
    },
  });

  const exportPDF = async () => {
    try {
      await exportAndDownload(
        () => api.exportContactPDF(id!),
        `contact-${id}.pdf`
      );
    } catch (err: any) {
      alert(err?.message ?? 'Export failed.');
    }
  };

  const submitNote = async () => {
    if (!newNote.trim()) return;
    if (newNote.length > 253) {
      setNoteError('Note must be 253 characters or less.');
      return;
    }
    try {
      await apiClient.post(`/contacts/${id}/notes`, { note_text: newNote });
      qc.invalidateQueries({ queryKey: ['contact', id] });
      setNewNote('');
      setNoteError('');
    } catch {
      setNoteError('Failed to save note.');
    }
  };

  const openResolveModal = (flagId: string) => {
    setResolveFlagId(flagId);
    setResolveNote('');
    setShowResolveModal(true);
  };

  const submitResolve = async () => {
    if (!resolveFlagId || !resolveNote.trim()) return;
    setResolving(true);
    try {
      await apiClient.post(`/flags/${resolveFlagId}/resolve`, { resolution_note: resolveNote });
      qc.invalidateQueries({ queryKey: ['contact', id] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['flags'] });
      qc.invalidateQueries({ queryKey: ['kpi'] });
      window.dispatchEvent(new Event('iad2:counts-stale'));
      setShowResolveModal(false);
      setResolveFlagId(null);
      setResolveNote('');
    } catch {
      alert('Failed to resolve flag.');
    } finally {
      setResolving(false);
    }
  };

  const canEdit = user?.role === 'Administrator' || user?.role === 'Superuser';
  const isAdmin = user?.role === 'Administrator';

  const submitArchive = async () => {
    if (!archiveReason.trim()) return;
    setArchiving(true);
    try {
      await api.archiveContact(id!, archiveReason.trim());
      qc.invalidateQueries({ queryKey: ['contact', id] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      setShowArchiveModal(false);
      navigate(-1);
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Failed to archive contact.');
    } finally {
      setArchiving(false);
    }
  };

  React.useEffect(() => {
    const handler = () => setShowRequestPopup(false);
    if (showRequestPopup) {
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }
  }, [showRequestPopup]);

  if (isLoading)
    return (
      <div style={{ padding: 32, color: 'var(--gray-500)', textAlign: 'center' }}>Loading…</div>
    );
  if (error || !contact)
    return (
      <div style={{ padding: 32, color: 'var(--red)', textAlign: 'center' }}>
        Contact not found.
      </div>
    );

  const openFlags = (contact.flags ?? []).filter((f) => f.status === 'Open');
  const isFlagged = contact.is_flagged === true || openFlags.length > 0;

  return (
    <div style={{ maxWidth: 860, paddingBottom: 40 }}>
      {/* Back */}
      <button
        className="btn btn-ghost"
        style={{ marginBottom: 12 }}
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={14} />
        Back
      </button>

      {/* Header */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <Avatar name={`${contact.first_name} ${contact.last_name}`} size={40} />
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>
                {contact.first_name} {contact.last_name}
              </h2>
              <span
                className={`pill ${contact.classification_id === 2 ? 'pill-restricted' : 'pill-standard'}`}
              >
                {contact.classification_id === 2 ? 'Restricted' : 'Standard'}
              </span>
              {contact.is_archived ? (
                <span className="pill" style={{ background: 'var(--amber-50)', color: 'var(--amber-text)' }}>
                  Archived
                </span>
              ) : !contact.is_active ? (
                <span className="pill" style={{ background: 'var(--red-50)', color: 'var(--red)' }}>
                  Inactive
                </span>
              ) : null}
              {isFlagged && (
                <span className="pill" style={{ background: 'var(--amber-50)', color: 'var(--amber-text)' }}>
                  {openFlags.length > 0 ? `${openFlags.length} Flag${openFlags.length > 1 ? 's' : ''}` : 'Flagged'}
                </span>
              )}
            </div>
            {contact.title && (
              <div style={{ fontSize: 14, color: 'var(--gray-600)', marginBottom: 2 }}>
                {contact.title}
              </div>
            )}
            <div style={{ fontSize: 14, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              {(contact.agency_name || contact.agency_acronym) && (
                <AgencyLogo
                  name={contact.agency_name ?? contact.agency_acronym ?? ''}
                  acronym={contact.agency_acronym ?? undefined}
                  size={32}
                />
              )}
              <span>
                {contact.agency_acronym
                  ? `${contact.agency_acronym} — ${contact.agency_name}`
                  : contact.agency_name}
                {contact.office_group && ` · ${contact.office_group}`}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canEdit && (
              <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>
                <Edit2 size={13} />
                Edit
              </button>
            )}
            <button className="btn btn-secondary" onClick={() => setShowSendModal(true)}>
              <Mail size={13} />
              Send Contact
            </button>
            <button className="btn btn-secondary" onClick={exportPDF}>
              <FileText size={13} />
              Export PDF
            </button>
            <div style={{ position: 'relative' }}>
              <button
                className="btn btn-secondary"
                onClick={(e) => { e.stopPropagation(); setShowRequestPopup((s) => !s); }}
              >
                Request Update
              </button>
              {showRequestPopup && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    zIndex: 50,
                    background: 'var(--white)',
                    border: '1px solid var(--gray-200)',
                    borderRadius: 8,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    minWidth: 220,
                    overflow: 'hidden',
                    marginTop: 4,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="popup-menu-btn"
                    onClick={() => {
                      setShowRequestPopup(false);
                      navigate(`/update-requests?create=1&agency_id=${contact.agency_id}&contact_id=${contact.id}`);
                    }}
                  >
                    <User size={13} />
                    This contact only
                  </button>
                  <button
                    className="popup-menu-btn"
                    onClick={() => {
                      setShowRequestPopup(false);
                      navigate(`/update-requests?create=1&agency_id=${contact.agency_id}`);
                    }}
                  >
                    <Building2 size={13} />
                    All contacts at {contact.agency_name || contact.agency_acronym}
                  </button>
                </div>
              )}
            </div>
            <button
              className="btn btn-secondary"
              style={isFlagged
                ? { background: 'var(--red)', color: 'white', border: '1px solid var(--red)' }
                : { color: 'var(--red)' }}
              onClick={() => {
                if (isFlagged && openFlags.length > 0) {
                  setResolveFlagId(openFlags[0].id);
                  setResolveNote('');
                  setShowResolveModal(true);
                } else if (!isFlagged) {
                  setShowFlagModal(true);
                }
              }}
            >
              <Flag size={13} />
              {isFlagged ? 'Flagged' : 'Flag'}
            </button>
            {isAdmin && !contact.is_archived && (
              <button
                className="btn btn-secondary"
                style={{ color: 'var(--red)', borderColor: '#fca5a5' }}
                onClick={() => { setArchiveReason(''); setShowArchiveModal(true); }}
              >
                Archive
              </button>
            )}
            {isAdmin && contact.is_archived && (
              <button
                className="btn btn-secondary"
                style={{ color: 'var(--green)', borderColor: '#86efac' }}
                onClick={async () => {
                  try {
                    await api.restoreContact(id!);
                    qc.invalidateQueries({ queryKey: ['contact', id] });
                    qc.invalidateQueries({ queryKey: ['contacts'] });
                  } catch (err: any) {
                    alert(err?.response?.data?.error ?? 'Failed to restore contact.');
                  }
                }}
              >
                Restore
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Agency procedures banner */}
      {((contact as any).agency_procedures_structured?.length > 0 || (contact as any).agency_procedures) && (
        <div style={{
          background: 'var(--amber-50)',
          border: '1px solid var(--amber)',
          borderRadius: 8,
          padding: '10px 14px',
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--amber-text)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Agency Contact Procedures
          </div>
          {(contact as any).agency_procedures_structured?.length > 0 ? (
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              {((contact as any).agency_procedures_structured as Array<{ step: number; procedure: string }>).map((s) => (
                <li key={s.step} style={{ fontSize: 14, color: 'var(--amber-text)', marginBottom: 4, lineHeight: 1.5 }}>
                  {s.procedure}
                </li>
              ))}
            </ol>
          ) : (
            <div style={{ fontSize: 14, color: 'var(--amber-text)', whiteSpace: 'pre-wrap' }}>
              {(contact as any).agency_procedures}
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--gray-200)', marginBottom: 16 }}>
        {(['overview', 'phones', 'roles', 'notes', 'flags', 'audit'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '8px 16px',
              background: 'none',
              border: 'none',
              borderBottom: tab === t ? '2px solid var(--blue)' : '2px solid transparent',
              color: tab === t ? 'var(--blue)' : 'var(--gray-600)',
              fontWeight: tab === t ? 600 : 400,
              cursor: 'pointer',
              fontSize: 14,
              textTransform: 'capitalize',
            }}
          >
            {t === 'flags' && isFlagged ? `flags (${openFlags.length || contact.open_flags_count || '!'})` : t}
          </button>
        ))}
      </div>

      {/* Verification status card — overview only */}
      {tab === 'overview' && (() => {
        const verifiedAt = contact.last_verified_at ? new Date(contact.last_verified_at) : null;
        const daysSince = (() => {
          if (!verifiedAt) return null;
          const now  = new Date(); now.setHours(0, 0, 0, 0);
          const then = new Date(verifiedAt); then.setHours(0, 0, 0, 0);
          return Math.floor((now.getTime() - then.getTime()) / (1000 * 60 * 60 * 24));
        })();
        const daysText = daysSince === null ? ''
          : daysSince <= 0 ? 'Today'
          : daysSince === 1 ? 'Yesterday'
          : `${daysSince} days ago`;
        const status = !verifiedAt ? 'Never Verified'
          : daysSince! <= agingDays ? 'Fresh'
          : daysSince! <= overdueDays ? 'Aging'
          : 'Overdue';
        const pillColor = status === 'Fresh' ? 'var(--green)'
          : status === 'Aging' ? 'var(--amber)'
          : status === 'Overdue' ? 'var(--red)'
          : 'var(--gray-500)';
        return (
          <div className="card" style={{ borderLeft: `4px solid ${pillColor}`, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ background: pillColor, color: 'white', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                {status}
              </span>
              <span style={{ fontSize: 14, color: 'var(--gray-600)' }}>
                {verifiedAt
                  ? `Last verified ${verifiedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (${daysText})`
                  : 'This contact has never been verified'}
              </span>
            </div>
            {canEdit && !contact.is_archived && (
              <button
                className="btn btn-secondary"
                style={{ fontSize: 14 }}
                onClick={async () => {
                  try {
                    await api.markContactVerified(id!);
                    qc.invalidateQueries({ queryKey: ['contact', id] });
                  } catch (err: any) {
                    alert(err?.response?.data?.error ?? 'Failed to mark as verified.');
                  }
                }}
              >
                <CheckCircle size={13} />
                Mark as Verified
              </button>
            )}
          </div>
        );
      })()}

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="card">
          <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px 24px' }}>
            {[
              ['First Name', contact.first_name],
              ['Last Name', contact.last_name],
              ['Title', contact.title],
              ['Agency', contact.agency_name],
              ['Office Group', contact.office_group],
              ['Email', contact.email_primary],
              ['Last Verified', contact.last_verified_at ? new Date(contact.last_verified_at).toLocaleDateString() : 'Never'],
              ['Verified By', contact.last_verified_by_name],
            ].map(([label, val]) => (
              <div key={label as string}>
                <dt style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {label}
                </dt>
                <dd style={{ margin: 0, fontSize: 14, color: val ? 'var(--gray-900)' : 'var(--gray-500)' }}>
                  {(val as string) ?? '—'}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {tab === 'phones' && (
        <div className="card">
          {(contact.phones ?? []).length === 0 ? (
            <p style={{ color: 'var(--gray-500)', margin: 0 }}>No phones on record.</p>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Number</th>
                  <th>Extension</th>
                  <th>Preferred</th>
                </tr>
              </thead>
              <tbody>
                {(contact.phones ?? []).map((p) => (
                  <tr key={p.id}>
                    <td>{PHONE_TYPES[p.phone_type_id] ?? p.phone_type_name ?? 'Unknown'}</td>
                    <td>
                      <a href={`tel:${p.phone_number}`} style={{ color: 'var(--blue)' }}>
                        {p.phone_number}
                      </a>
                    </td>
                    <td>{p.extension ?? '—'}</td>
                    <td>
                      {p.is_preferred ? (
                        <CheckCircle size={15} color="var(--green)" />
                      ) : (
                        <span style={{ color: 'var(--gray-300)' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'roles' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Assigned Roles</span>
            {canEdit && (
              <button
                className="btn btn-secondary"
                style={{ fontSize: 12, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                onClick={() => setShowEditRolesModal(true)}
              >
                <Tag size={12} /> Edit Roles
              </button>
            )}
          </div>
          {contactRoles.length === 0 ? (
            <p style={{ color: 'var(--gray-500)', margin: 0 }}>No roles assigned.</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {contactRoles.map((r: any) => (
                <span
                  key={r.id}
                  style={{
                    background: 'var(--navy)',
                    color: 'white',
                    fontSize: 12,
                    padding: '3px 10px',
                    borderRadius: 12,
                    fontWeight: 500,
                  }}
                >
                  {r.role_name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'notes' && (
        <div>
          {canEdit && (
            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note… (253 char max)"
                  maxLength={253}
                  rows={2}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    border: '1px solid var(--gray-300)',
                    borderRadius: 6,
                    fontSize: 14,
                    resize: 'vertical',
                  }}
                />
                <button className="btn btn-primary" onClick={submitNote}>
                  <Plus size={13} />
                  Add
                </button>
              </div>
              {noteError && <p style={{ color: 'var(--red)', fontSize: 12, margin: '6px 0 0' }}>{noteError}</p>}
            </div>
          )}
          {(contact.notes ?? []).length === 0 ? (
            <div className="card" style={{ color: 'var(--gray-500)' }}>No notes yet.</div>
          ) : (
            (contact.notes ?? []).map((n: Note) => (
              <div
                key={n.id}
                className="card"
                style={{ marginBottom: 8, padding: '10px 14px' }}
              >
                <div style={{ fontSize: 14, marginBottom: 4 }}>{n.note_text}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                  {n.created_by} · {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'flags' && (
        <div>
          {(contact.flags ?? []).length === 0 ? (
            <div className="card" style={{ color: 'var(--gray-500)' }}>No flags.</div>
          ) : (
            (contact.flags ?? []).map((f: FlagType) => (
              <div
                key={f.id}
                className="card"
                style={{ marginBottom: 8, padding: '10px 14px', borderLeft: `3px solid ${f.status === 'Open' ? 'var(--red)' : 'var(--green)'}` }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{f.flag_reason}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                      By {f.flagged_by_name ?? 'unknown'} · {new Date(f.created_at).toLocaleDateString()}
                    </div>
                    {f.resolution_note && (
                      <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 6 }}>
                        Resolution: {f.resolution_note}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      className="pill"
                      style={{
                        background: f.status === 'Open' ? 'var(--red-50)' : 'var(--green-50)',
                        color: f.status === 'Open' ? 'var(--red)' : 'var(--green)',
                      }}
                    >
                      {f.status}
                    </span>
                    {f.status === 'Open' && canEdit && (
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: 12, padding: '4px 10px' }}
                        onClick={() => openResolveModal(f.id)}
                      >
                        Resolve
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'audit' && (
        <div className="card" style={{ padding: 0 }}>
          {(contact.audit_log ?? []).length === 0 ? (
            <div style={{ padding: 20, color: 'var(--gray-500)' }}>No audit history.</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Details</th>
                  <th>Changed By</th>
                  <th>Date</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {(contact.audit_log ?? []).map((a: AuditEntry) => {
                  const nv = a.new_value as Record<string, unknown> | undefined;
                  const ov = a.old_value as Record<string, unknown> | undefined;
                  let detailNode: React.ReactNode = '—';

                  if (nv?.reason) {
                    detailNode = `Reason: ${nv.reason}`;
                  } else if (nv?.resolution_note) {
                    detailNode = `Resolution: ${nv.resolution_note}`;
                  } else if (ov && nv && !nv.diff && !nv.reason) {
                    // Pending change approval — show field-level before/after
                    const fields = Object.keys(nv);
                    if (fields.length) {
                      detailNode = (
                        <div style={{ lineHeight: 1.6 }}>
                          {fields.map((f) => (
                            <div key={f}>
                              <span style={{ fontWeight: 500 }}>{f.replace(/_/g, ' ')}</span>:{' '}
                              <span style={{ color: 'var(--red)', textDecoration: 'line-through' }}>
                                {String(ov[f] ?? '—')}
                              </span>
                              {' → '}
                              <span style={{ color: 'var(--green)' }}>{String(nv[f] ?? '—')}</span>
                            </div>
                          ))}
                        </div>
                      );
                    }
                  } else if (a.notes?.startsWith('Approved from')) {
                    detailNode = a.notes;
                  }

                  const actionLabel = (() => {
                    const name = a.action_name ?? String(a.action_id);
                    if (name === 'VERIFY') return (
                      <span style={{ color: 'var(--green)', fontWeight: 600 }}>✓ Marked as Verified</span>
                    );
                    return name;
                  })();

                  return (
                    <tr key={a.id}>
                      <td>{actionLabel}</td>
                      <td style={{ fontSize: 12, color: 'var(--gray-600)' }}>{detailNode}</td>
                      <td>{a.changed_by_name ?? '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        {new Date(a.changed_at).toLocaleString()}
                      </td>
                      <td style={{ fontSize: 12 }}>{a.source ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Flag Modal */}
      {showFlagModal && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowFlagModal(false); }}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Flag Contact</h3>
              <button className="btn btn-ghost" onClick={() => setShowFlagModal(false)}>
                <X size={16} />
              </button>
            </div>
            <label className="field-label">Reason</label>
            <textarea
              className="field-input"
              rows={3}
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              placeholder="Describe the issue…"
              maxLength={500}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setShowFlagModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={!flagReason.trim() || flagMutation.isPending}
                onClick={() => flagMutation.mutate()}
              >
                {flagMutation.isPending ? 'Flagging…' : 'Submit Flag'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Contact Modal */}
      {showSendModal && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowSendModal(false); }}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Send Contact Info</h3>
              <button className="btn btn-ghost" onClick={() => setShowSendModal(false)}>
                <X size={16} />
              </button>
            </div>
            <label className="field-label">Recipient Email</label>
            <input
              type="email"
              className="field-input"
              value={sendEmail}
              onChange={(e) => setSendEmail(e.target.value)}
              placeholder="recipient@example.gov"
            />
            <label className="field-label" style={{ marginTop: 12 }}>
              Message (optional)
            </label>
            <textarea
              className="field-input"
              rows={3}
              value={sendMsg}
              onChange={(e) => setSendMsg(e.target.value)}
              placeholder="Add a note to the email…"
              maxLength={1000}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setShowSendModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={!sendEmail.trim() || sendMutation.isPending}
                onClick={() => sendMutation.mutate()}
              >
                {sendMutation.isPending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Contact Modal */}
      {showEditModal && (
        <EditContactModal
          contact={contact}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showEditRolesModal && (
        <EditRolesModal
          contactId={id!}
          allRoles={allRoles}
          currentRoleIds={contactRoles.map((r: any) => r.role_id)}
          onClose={() => setShowEditRolesModal(false)}
          onSaved={() => { refetchContactRoles(); setShowEditRolesModal(false); }}
        />
      )}

      {/* Resolve Flag Modal */}
      {showResolveModal && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowResolveModal(false); }}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Resolve Flag</h3>
              <button className="btn btn-ghost" onClick={() => setShowResolveModal(false)}><X size={16} /></button>
            </div>
            <label className="field-label">Resolution Note</label>
            <textarea
              className="field-input"
              rows={3}
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              placeholder="Describe how this flag was resolved…"
              maxLength={1000}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setShowResolveModal(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--green)' }}
                disabled={!resolveNote.trim() || resolving}
                onClick={submitResolve}
              >
                {resolving ? 'Resolving…' : 'Mark Resolved'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      {showArchiveModal && (
        <div className="modal-overlay" onClick={() => setShowArchiveModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Archive Contact</h3>
              <button className="btn btn-ghost" onClick={() => setShowArchiveModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0, color: 'var(--gray-600)', fontSize: 14 }}>
                Archiving will remove <strong>{contact.first_name} {contact.last_name}</strong> from
                the active directory. This action can be reversed by an Administrator.
              </p>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                Reason for archiving *
              </label>
              <select
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray-300)', fontSize: 14 }}
              >
                <option value="">— Select a reason —</option>
                <option value="Left agency">Left agency</option>
                <option value="Retired">Retired</option>
                <option value="Transferred">Transferred to another agency</option>
                <option value="Duplicate record">Duplicate record</option>
                <option value="Data error">Data error / incorrectly created</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowArchiveModal(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
                disabled={!archiveReason || archiving}
                onClick={submitArchive}
              >
                {archiving ? 'Archiving…' : 'Archive Contact'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactDetailPage;
